import { type Player, type GameAction } from '../../../types/game';

const THREE_FOUL_PENALTY = 15;

interface UseGameActionsProps {
  playerData: Player[];
  activePlayerIndex: number;
  currentRun: number;
  ballsOnTable: number;
  actions: GameAction[];
  gameId: string;
  currentInning: number;
  saveGameToSupabase: (
    gameId: string,
    players: Player[],
    actions: GameAction[],
    completed: boolean,
    winner_id: number | null,
    turnStartTime?: Date,
  ) => void;
  setPlayerData: (data: Player[]) => void;
  setActions: (actions: GameAction[]) => void;
  setBallsOnTable: (count: number) => void;
  setCurrentRun: (run: number) => void;
  setActivePlayerIndex: (index: number) => void;
  setTurnStartTime: (time: Date | null) => void;
  setCurrentInning: (inning: number) => void;
  setGameWinner: (winner: Player | null) => void;
  setShowEndGameModal: (show: boolean) => void;
  setPlayerNeedsReBreak: (playerId: number | null) => void;
  setShowAlertModal: (show: boolean) => void;
  setAlertMessage: (message: string) => void;
  setIsUndoEnabled: (enabled: boolean) => void;
  playerNeedsReBreak: number | null;
  setMatchEndTime: (endTime: Date | null) => void;
}

interface AddFoulOptions {
  manualConsecutiveDecision?: 'regular' | 'threeFoul';
  playerIdOverride?: number;
}

export const useGameActions = ({
  playerData,
  activePlayerIndex,
  currentRun,
  ballsOnTable,
  actions,
  gameId,
  currentInning,
  saveGameToSupabase,
  setPlayerData,
  setActions,
  setBallsOnTable,
  setCurrentRun,
  setActivePlayerIndex,
  setTurnStartTime,
  setCurrentInning,
  setGameWinner,
  setShowEndGameModal,
  setPlayerNeedsReBreak,
  setShowAlertModal,
  setAlertMessage,
  setIsUndoEnabled,
  playerNeedsReBreak,
  setMatchEndTime,
}: UseGameActionsProps) => {
  const resolveNextTableState = (botsValue: number) => (botsValue === 0 ? 15 : botsValue);

  const handleAddScore = (score: number, botsValue?: number) => {
    if (botsValue === undefined) {
      return { needsBOTInput: true, action: 'newrack' };
    }

    const ballsPocketed = Math.max(0, ballsOnTable - botsValue);
    const pointsScored = ballsPocketed;

    const newAction: GameAction = {
      type: 'score',
      playerId: playerData[activePlayerIndex].id,
      value: pointsScored,
      timestamp: new Date(),
      ballsOnTable: 15,
    };

    if (playerNeedsReBreak === playerData[activePlayerIndex].id) {
      setPlayerNeedsReBreak(null);
    }

    setBallsOnTable(15);
    setActions([...actions, newAction]);
    setIsUndoEnabled(true);

    const updatedPlayerData = [...playerData];
    updatedPlayerData[activePlayerIndex].score += pointsScored;
    updatedPlayerData[activePlayerIndex].consecutiveFouls = 0;

    const newCurrentRun = currentRun + pointsScored;
    setCurrentRun(newCurrentRun);
    if (newCurrentRun > updatedPlayerData[activePlayerIndex].highRun) {
      updatedPlayerData[activePlayerIndex].highRun = newCurrentRun;
    }

    setPlayerData(updatedPlayerData);
    saveGameToSupabase(gameId, updatedPlayerData, [...actions, newAction], false, null);

    return { needsBOTInput: false };
  };

  const handleAddFoul = (
    botsValue?: number,
    foulPenalty?: number,
    options?: AddFoulOptions,
  ) => {
    if (botsValue === undefined) {
      return { needsBOTInput: true, action: 'foul' };
    }

    const actingPlayerIndex =
      options?.playerIdOverride !== undefined
        ? playerData.findIndex((player) => player.id === options.playerIdOverride)
        : activePlayerIndex;
    const playerIndex = actingPlayerIndex === -1 ? activePlayerIndex : actingPlayerIndex;

    const isThirdConsecutiveFoul = playerData[playerIndex].consecutiveFouls === 2;

    // Check if this is the first action of the game (i.e., the opening break)
    // or if the player needs to re-break
    const isOpeningBreak =
      (actions.length === 0 && currentInning === 1) ||
      playerNeedsReBreak === playerData[playerIndex].id;

    // Use the provided penalty, or default to -2 for break fouls, -1 for regular
    const penaltyValue =
      foulPenalty !== undefined ? -Math.abs(foulPenalty) : isOpeningBreak ? -2 : -1;

    const manualDecision = options?.manualConsecutiveDecision;
    const isManualThreeFoul = manualDecision === 'threeFoul';

    const newAction: GameAction = {
      type: 'foul',
      playerId: playerData[playerIndex].id,
      value: penaltyValue,
      timestamp: new Date(),
      ballsOnTable: botsValue,
      reBreak: isThirdConsecutiveFoul || isManualThreeFoul,
      isBreakFoul: isOpeningBreak,
    };

    const ballsPocketed = Math.max(0, ballsOnTable - botsValue);
    setBallsOnTable(resolveNextTableState(botsValue));
    setActions([...actions, newAction]);
    setIsUndoEnabled(true);

    const nextPlayerIndex = (playerIndex + 1) % playerData.length;
    const updatedPlayerData = [...playerData];

    const hasScoreActions = actions.some(
      (action) =>
        action.type === 'score' && action.playerId === playerData[playerIndex].id,
    );

    const totalToAdd = hasScoreActions ? ballsPocketed : currentRun + ballsPocketed;
    updatedPlayerData[playerIndex].score += totalToAdd;

    if (totalToAdd > updatedPlayerData[playerIndex].highRun) {
      updatedPlayerData[playerIndex].highRun = totalToAdd;
    }

    const previousConsecutiveFouls = updatedPlayerData[playerIndex].consecutiveFouls;
    updatedPlayerData[playerIndex].consecutiveFouls = previousConsecutiveFouls + 1;
    updatedPlayerData[playerIndex].fouls += 1;

    if (manualDecision === 'regular') {
      updatedPlayerData[playerIndex].consecutiveFouls = 1;
    }

    const shouldApplyThreeFoulPenalty =
      isManualThreeFoul ||
      (!manualDecision && updatedPlayerData[playerIndex].consecutiveFouls === 3);

    // Check for three consecutive fouls (automatic or manual override)
    if (shouldApplyThreeFoulPenalty) {
      // Apply the foul penalty first (use the actual penalty value from the action)
      const actualPenalty = Math.abs(penaltyValue);
      updatedPlayerData[playerIndex].score += penaltyValue;

      // Then apply the additional 15-point penalty for three consecutive fouls
      updatedPlayerData[playerIndex].score -= THREE_FOUL_PENALTY;
      updatedPlayerData[playerIndex].consecutiveFouls = 0;
      setBallsOnTable(15);
      setPlayerNeedsReBreak(updatedPlayerData[playerIndex].id);

      const playerName = updatedPlayerData[playerIndex].name;
      const prefix =
        isManualThreeFoul && previousConsecutiveFouls < 2
          ? `${playerName} has been assessed a manual three-foul penalty. `
          : `${playerName} has committed three consecutive fouls! `;
      setAlertMessage(
        `${prefix}${actualPenalty + THREE_FOUL_PENALTY}-point penalty applied (${
          actualPenalty
        } for ${isOpeningBreak ? 'break ' : ''}foul + ${THREE_FOUL_PENALTY} for three consecutive fouls). ${playerName} must re-break all 15 balls under opening break requirements.`,
      );
      setShowAlertModal(true);
    }
    // Handle break fouls that are not three consecutive fouls
    else if (isOpeningBreak) {
      // Apply the penalty (already negative)
      updatedPlayerData[playerIndex].score += penaltyValue;

      const breakerName = updatedPlayerData[playerIndex].name;
      const incomingPlayerName = updatedPlayerData[nextPlayerIndex].name;
      const actualPenalty = Math.abs(penaltyValue);

      setAlertMessage(
        `${breakerName} fouled on the break! ${actualPenalty}-point penalty applied. ${incomingPlayerName} can choose to accept the table as-is or require ${breakerName} to break again (with the same foul penalties if they foul again).`,
      );
      setShowAlertModal(true);

      // Display warning if they now have two consecutive fouls
      if (updatedPlayerData[playerIndex].consecutiveFouls === 2) {
        const playerName = updatedPlayerData[playerIndex].name;
        setAlertMessage(
          `Warning: ${playerName} has two consecutive fouls. A third consecutive foul will result in a 15-point penalty plus the regular foul deduction.`,
        );
        setShowAlertModal(true);
      }

      // Keep the same player as the active player in case a re-break is chosen
      // The actual player switch will be handled by the UI after the incoming player makes their choice
    }
    // Handle regular fouls (not break fouls and not three consecutive)
    else {
      updatedPlayerData[playerIndex].score -= 1;

      if (updatedPlayerData[playerIndex].consecutiveFouls === 2) {
        const playerName = updatedPlayerData[playerIndex].name;
        setAlertMessage(
          `Warning: ${playerName} has two consecutive fouls. A third consecutive foul will result in a 15-point penalty plus the regular 1-point deduction.`,
        );
        setShowAlertModal(true);
      }
    }

    setCurrentRun(0);

    // Only switch players if it's not a break foul or a three-foul situation
    // For break fouls, we'll let the UI handle it after the incoming player makes their choice
    let newTurnStartTime: Date | undefined;
    if (!isOpeningBreak && !shouldApplyThreeFoulPenalty) {
      if (nextPlayerIndex === 0) {
        setCurrentInning(currentInning + 1);
      }
      updatedPlayerData[nextPlayerIndex].innings += 1;
      setActivePlayerIndex(nextPlayerIndex);
      newTurnStartTime = new Date();
      setTurnStartTime(newTurnStartTime);
    }

    setPlayerData(updatedPlayerData);

    const playerTargetScore = updatedPlayerData[playerIndex].targetScore;
    if (updatedPlayerData[playerIndex].score >= playerTargetScore) {
      const winner = { ...updatedPlayerData[playerIndex] };
      setGameWinner(winner);
      setMatchEndTime(new Date());
      setShowEndGameModal(true);
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        true,
        winner.id,
        newTurnStartTime,
      );
    } else {
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        false,
        null,
        newTurnStartTime,
      );
    }

    return { needsBOTInput: false };
  };

  const handleAddSafety = (botsValue?: number) => {
    if (botsValue === undefined) {
      return { needsBOTInput: true, action: 'safety' };
    }

    const newAction: GameAction = {
      type: 'safety',
      playerId: playerData[activePlayerIndex].id,
      value: 0,
      timestamp: new Date(),
      ballsOnTable: botsValue,
    };

    if (playerNeedsReBreak === playerData[activePlayerIndex].id) {
      setPlayerNeedsReBreak(null);
    }

    const ballsPocketed = Math.max(0, ballsOnTable - botsValue);
    setBallsOnTable(resolveNextTableState(botsValue));
    setActions([...actions, newAction]);
    setIsUndoEnabled(true);

    const nextPlayerIndex = (activePlayerIndex + 1) % playerData.length;
    const updatedPlayerData = [...playerData];

    updatedPlayerData[activePlayerIndex].safeties += 1;
    updatedPlayerData[activePlayerIndex].consecutiveFouls = 0;

    const hasScoreActions = actions.some(
      (action) =>
        action.type === 'score' && action.playerId === playerData[activePlayerIndex].id,
    );

    const totalToAdd = hasScoreActions ? ballsPocketed : currentRun + ballsPocketed;
    updatedPlayerData[activePlayerIndex].score += totalToAdd;

    if (totalToAdd > updatedPlayerData[activePlayerIndex].highRun) {
      updatedPlayerData[activePlayerIndex].highRun = totalToAdd;
    }

    setCurrentRun(0);

    if (nextPlayerIndex === 0) {
      setCurrentInning(currentInning + 1);
    }
    updatedPlayerData[nextPlayerIndex].innings += 1;
    setActivePlayerIndex(nextPlayerIndex);
    const newTurnStartTime = new Date();
    setTurnStartTime(newTurnStartTime);

    setPlayerData(updatedPlayerData);

    const playerTargetScore = updatedPlayerData[activePlayerIndex].targetScore;
    if (updatedPlayerData[activePlayerIndex].score >= playerTargetScore) {
      const winner = { ...updatedPlayerData[activePlayerIndex] };
      setGameWinner(winner);
      setMatchEndTime(new Date());
      setShowEndGameModal(true);
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        true,
        winner.id,
        newTurnStartTime,
      );
    } else {
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        false,
        null,
        newTurnStartTime,
      );
    }

    return { needsBOTInput: false };
  };

  const handleAddMiss = (botsValue?: number) => {
    if (botsValue === undefined) {
      return { needsBOTInput: true, action: 'miss' };
    }

    const newAction: GameAction = {
      type: 'miss',
      playerId: playerData[activePlayerIndex].id,
      value: 0,
      timestamp: new Date(),
      ballsOnTable: botsValue,
    };

    if (playerNeedsReBreak === playerData[activePlayerIndex].id) {
      setPlayerNeedsReBreak(null);
    }

    const ballsPocketed = Math.max(0, ballsOnTable - botsValue);
    setBallsOnTable(resolveNextTableState(botsValue));
    setActions([...actions, newAction]);
    setIsUndoEnabled(true);

    const nextPlayerIndex = (activePlayerIndex + 1) % playerData.length;
    const updatedPlayerData = [...playerData];

    updatedPlayerData[activePlayerIndex].missedShots += 1;
    updatedPlayerData[activePlayerIndex].consecutiveFouls = 0;

    const hasScoreActions = actions.some(
      (action) =>
        action.type === 'score' && action.playerId === playerData[activePlayerIndex].id,
    );

    const totalToAdd = hasScoreActions ? ballsPocketed : currentRun + ballsPocketed;
    updatedPlayerData[activePlayerIndex].score += totalToAdd;

    if (totalToAdd > updatedPlayerData[activePlayerIndex].highRun) {
      updatedPlayerData[activePlayerIndex].highRun = totalToAdd;
    }

    setCurrentRun(0);

    if (nextPlayerIndex === 0) {
      setCurrentInning(currentInning + 1);
    }
    updatedPlayerData[nextPlayerIndex].innings += 1;
    setActivePlayerIndex(nextPlayerIndex);
    const newTurnStartTime = new Date();
    setTurnStartTime(newTurnStartTime);

    setPlayerData(updatedPlayerData);

    const playerTargetScore = updatedPlayerData[activePlayerIndex].targetScore;
    if (updatedPlayerData[activePlayerIndex].score >= playerTargetScore) {
      const winner = { ...updatedPlayerData[activePlayerIndex] };
      setGameWinner(winner);
      setMatchEndTime(new Date());
      setShowEndGameModal(true);
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        true,
        winner.id,
        newTurnStartTime,
      );
    } else {
      saveGameToSupabase(
        gameId,
        updatedPlayerData,
        [...actions, newAction],
        false,
        null,
        newTurnStartTime,
      );
    }

    return { needsBOTInput: false };
  };

  return {
    handleAddScore,
    handleAddFoul,
    handleAddSafety,
    handleAddMiss,
  };
};
